import React, { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

//
import IconWrapper from "../icon-wrapper/IconWrapper";
import BRANCH_CONTACTS from "../../assets/data/branch-contacts";

export default function NavbarPhoneContacts() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const dropdownId = useId();

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  function deviceSupportsHover() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function openDropdown() {
    clearCloseTimer();
    setIsOpen(true);
  }

  function closeDropdown() {
    clearCloseTimer();
    setIsOpen(false);
  }

  function scheduleCloseDropdown() {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 1500);
  }

  function handlePointerEnter(event) {
    if (event.pointerType === "mouse") {
      openDropdown();
    }
  }

  function handlePointerLeave(event) {
    if (event.pointerType === "mouse") {
      scheduleCloseDropdown();
    }
  }

  function handleToggle(event) {
    if (!deviceSupportsHover() || event.detail === 0) {
      if (isOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
    }
  }

  function handleBlur(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      closeDropdown();
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      closeDropdown();
      event.currentTarget.querySelector("button")?.focus();
    }
  }

  return (
    <StyledNavbarPhoneContacts
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="nav-phone__trigger"
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        aria-label={t("navPhoneContacts")}
        onClick={handleToggle}
      >
        <span className="nav-phone__icon">
          <IconWrapper
            bg="--light-blue"
            w={36}
            h={36}
            icon="phone-270rotated"
            iconW={14}
            iconH={14}
            iconBg="--white"
            animation="nav"
          />
        </span>
      </button>

      {isOpen && (
        <div
          id={dropdownId}
          className="nav-phone__dropdown"
          aria-label={t("navPhoneContacts")}
        >
          {BRANCH_CONTACTS.map((branch) => (
            <section className="nav-phone__branch" key={branch.id}>
              <h2>{t(branch.title)}</h2>
              <ul>
                {branch.phones.map((phone) => (
                  <li key={phone.id}>
                    {phone.label && <span>{t(phone.label)}</span>}
                    <a
                      href={phone.href}
                      aria-label={
                        phone.label
                          ? `${t(phone.label)}: ${phone.phone}`
                          : phone.phone
                      }
                    >
                      {phone.phone}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </StyledNavbarPhoneContacts>
  );
}

const StyledNavbarPhoneContacts = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 0;
    width: 100%;
    height: 14px;
  }

  .nav-phone {
    &__trigger {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--white);
      font: inherit;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--light-blue);
        outline-offset: 5px;
        border-radius: 4px;
      }
    }

    &__icon {
      display: flex;
      flex-shrink: 0;
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + 14px);
      right: 0;
      z-index: 20;
      width: min(360px, calc(100vw - 32px));
      padding: 20px;
      border: 1px solid rgba(0, 87, 255, 0.1);
      border-radius: 14px;
      background-color: var(--white);
      box-shadow: 0 14px 36px rgba(8, 24, 64, 0.16);
    }

    &__branch {
      padding-bottom: 18px;
      margin-bottom: 18px;
      border-bottom: 1px solid rgba(0, 33, 82, 0.08);

      &:last-child {
        padding-bottom: 0;
        margin-bottom: 0;
        border-bottom: 0;
      }

      h2 {
        margin-bottom: 12px;
        color: var(--dark-blue);
        font-size: 16px;
        font-weight: 600;
        line-height: 1.35;
      }

      ul {
        display: grid;
        gap: 12px;
      }

      li {
        display: grid;
        gap: 4px;

        span {
          color: #72809a;
          font-size: 12px;
          font-weight: 500;
          line-height: 1.4;
        }

        a {
          width: fit-content;
          color: var(--light-blue);
          font-size: 16px;
          font-weight: 600;
          line-height: 1.5;
          text-decoration: none;

          &:hover,
          &:focus-visible {
            text-decoration: underline;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .nav-phone {
      &__dropdown {
        padding: 18px;
      }

      &__branch {
        a {
          min-height: 44px;
          display: inline-flex;
          align-items: center;
        }
      }
    }
  }
`;
